function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { List, fromJS } from 'immutable';
import { of, pipe } from 'rxjs';
import { complement } from 'ramda';
import { isUpcoming, isInternational, isDomestic, isBulk, isBalanceTransfer, setUpcomingFlag, sortByActivityDate, sortByDueDate } from './payment-utils';
import { HistoryRecord, buildHistoryRecordsFromAudits } from './audit-history';
import { normalizeDate } from './date-utils';
import { hasChannel } from './consent-utils';
var GROUPING_NAMES = ['ACTIVE', 'DECLINED', 'BLOCKED'];
var ACTIVE_STATUSES = ['ACCEPTED', 'AUTHORIZED', 'CONSUMED'];
var GROUPINGS = GROUPING_NAMES.reduce(function (groupings, grouping) {
  return Object.assign({}, groupings, _defineProperty({}, grouping, grouping));
}, {});
var ACTIVITY_NAMES = ['SUBMITTING', 'SUBMITTED', 'FAILED', 'SUCCEEDED', 'REJECTED'];
var ACTIVITIES = ACTIVITY_NAMES.reduce(function (activities, activity) {
  return Object.assign({}, activities, _defineProperty({}, activity, activity));
}, {});
var STATUS_NAMES = [].concat(ACTIVITY_NAMES, GROUPING_NAMES, ACTIVE_STATUSES);
var STATUSES = STATUS_NAMES.reduce(function (statuses, status) {
  return Object.assign({}, statuses, _defineProperty({}, status, status));
}, {});
function getPaymentStatusLabel(status) {
  var STATUS_LABELS = {
    ACCEPTED: 'Created',
    AUTHORIZED: 'Payment processed',
    CONSUMED: 'Payment processed',
    DECLINED: 'Payment declined',
    SUBMITTING: 'Payment initiated',
    SUBMITTED: 'Payment in progress',
    FAILED: 'Payment failed',
    SUCCEEDED: 'Payment successful',
    BLOCKED: 'Request blocked by Bank',
    REJECTED: 'Payment rejected'
  };
  return STATUS_LABELS[status];
}
function getBulkStatusLabel(status) {
  var STATUS_LABELS = {
    ACCEPTED: 'Bulk file created',
    AUTHORIZED: 'Bulk file processed',
    CONSUMED: 'Bulk file processed',
    DECLINED: 'Bulk file declined',
    SUBMITTING: 'Bulk file submission',
    SUBMITTED: 'Bulk file in progress',
    FAILED: 'Bulk file submission failed',
    SUCCEEDED: 'Bulk file submitted',
    BLOCKED: 'Bulk file blocked',
    REJECTED: 'Bulk file rejected'
  };
  return STATUS_LABELS[status];
}
function getBalanceTransferStatusLabel(status) {
  var STATUS_LABELS = {
    ACCEPTED: 'Created',
    AUTHORIZED: 'Balance transfer processed',
    CONSUMED: 'Balance transfer processed',
    DECLINED: 'Balance transfer declined',
    SUBMITTING: 'Balance transfer initiated',
    SUBMITTED: 'Balance transfer in progress',
    FAILED: 'Balance transfer failed',
    SUCCEEDED: 'Balance transfer successful',
    BLOCKED: 'Request blocked by Bank',
    REJECTED: 'Balance transfer rejected'
  };
  return STATUS_LABELS[status];
}
function isAuthorizedPaymentStatus(status) {
  return status === 'AUTHORIZED' || status === 'CONSUMED';
}
function paymentStatus(payment) {
  var grouping = payment.grouping,
    status = payment.status;
  return grouping !== 'ACTIVE' ? grouping : status === 'ACCEPTED' ? 'ACCEPTED' : payment.getIn(['activity', 'status'], status);
}
function toPaymentStatusLabel(payment) {
  return isBulk(payment) ? getBulkStatusLabel(paymentStatus(payment)) : getPaymentStatusLabel(paymentStatus(payment));
}
function toPaymentTypeLabel(payment) {
  var isPbb = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
  if (isInternational(payment)) return 'International';
  if (isBulk(payment)) return isPbb ? 'Bulk' : 'Batch';
  if (isBalanceTransfer(payment)) return 'Balance Transfer';
  if (isDomestic(payment)) return 'Domestic';
}
function toPbbPaymentTypeLabel(payment) {
  return toPaymentTypeLabel(payment, true);
}
function toCpbPaymentTypeLabel(payment) {
  return toPaymentTypeLabel(payment, false);
}
var updatePaymentsGeneratedState = function updatePaymentsGeneratedState(state) {
  var all = Array.from(state.getIn(['payments', 'index']).values());
  var upcoming = all.filter(isUpcoming).sort(sortByDueDate);
  var past = all.filter(complement(isUpcoming)).sort(sortByActivityDate);
  var upcomingSinglePayments = upcoming.filter(hasChannel('OBPISP'));
  var pastSinglePayments = past.filter(hasChannel('OBPISP'));
  var newPaymentEntries = fromJS({
    upcomingDomestic: upcomingSinglePayments.filter(isDomestic),
    pastDomestic: pastSinglePayments.filter(isDomestic),
    upcomingInternational: upcomingSinglePayments.filter(isInternational),
    pastInternational: pastSinglePayments.filter(isInternational),
    upcomingBulk: upcoming.filter(hasChannel('OBPISPBB')),
    pastBulk: past.filter(hasChannel('OBPISPBB')),
    pastBalanceTransfer: pastSinglePayments.filter(isBalanceTransfer)
  });
  return state.mergeIn(['payments'], newPaymentEntries);
};
var normalizeExecutionDate = function normalizeExecutionDate(payment) {
  return payment.updateIn(['payload', 'executionDate'], normalizeDate);
};
var normalizeEstimatedArrivalDate = function normalizeEstimatedArrivalDate(payment) {
  return payment.updateIn(['payload', 'internationalPaymentDetails', 'pimsCreditDate'], normalizeDate);
};
var normalizeDates = pipe(normalizeExecutionDate, normalizeEstimatedArrivalDate);
var updatePaymentsGeneratedState$ = of(updatePaymentsGeneratedState);
var updatePaymentHistoryState = function updatePaymentHistoryState(paymentId, activityAudits) {
  return function (state) {
    return state.updateIn(['payments', 'index', paymentId], function (payment) {
      return payment.set('history', getHistory(payment, activityAudits).reverse());
    }).update(updatePaymentsGeneratedState);
  };
};
var updatePaymentActivityState = function updatePaymentActivityState(paymentId, activityAudits) {
  return function (state) {
    return state.updateIn(['payments', 'index', paymentId], function (payment) {
      if (activityAudits.size > 0) {
        var changeAt = activityAudits.reverse().getIn([0, 'changeAt']);
        return payment.setIn(['activity', 'changeAt'], changeAt);
      }
      return payment;
    }).update(updatePaymentsGeneratedState);
  };
};
function getHistory(payment, activityAudits) {
  var createdAt = payment.createdAt,
    modifiedAt = payment.modifiedAt,
    status = payment.status;
  var isAccepted = status === 'ACCEPTED';
  var isBulkPayment = isBulk(payment);
  var isTransfer = isBalanceTransfer(payment);
  var consentHistory = List.of(new HistoryRecord({
    date: isAccepted ? createdAt : modifiedAt,
    label: isBulkPayment ? getBulkStatusLabel(status) : isTransfer ? getBalanceTransferStatusLabel(status) : getPaymentStatusLabel(status),
    status: status
  }));
  return isAuthorizedPaymentStatus(status) ? consentHistory.concat(buildHistoryRecordsFromAudits(activityAudits, isBulkPayment ? getBulkStatusLabel : isTransfer ? getBalanceTransferStatusLabel : getPaymentStatusLabel)) : consentHistory;
}
function paymentDescriptiveDate(payment) {
  var status = paymentStatus(payment);
  switch (status) {
    case 'ACCEPTED':
      return payment.createdAt;
    case 'AUTHORIZED':
    case 'CONSUMED':
    case 'BLOCKED':
    case 'DECLINED':
      return payment.modifiedAt;
    default:
      return payment.getIn(['activity', 'changeAt']);
  }
}
var processPaymentConsents = function processPaymentConsents(consents) {
  return consents.map(setUpcomingFlag).map(normalizeDates);
};
export { ACTIVITIES, GROUPINGS, STATUSES, processPaymentConsents, HistoryRecord, isAuthorizedPaymentStatus, toPaymentStatusLabel, toPbbPaymentTypeLabel, toCpbPaymentTypeLabel, paymentStatus, paymentDescriptiveDate, updatePaymentsGeneratedState$, updatePaymentActivityState, updatePaymentHistoryState };